/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classnames from 'classnames';

import { DropdownSelector } from '@bingads-webui-react/dropdown-selector';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import ValidationLegacyStrings from '@bingads-webui-clientcenter/validation-legacy-strings';
import { Input, Select } from '@bingads-webui-react/primitive-with-validation-and-signal';
import { LineByLineFormGroup, InputFormGroup, StaticFormGroup } from '@bingads-webui-cc-react/layout';
import { Metadata } from '@bingads-webui-clientcenter/address';
import { Constants } from '@bingads-webui-clientcenter/entity-utils';
import { merge, createAsyncToken } from '@bingads-webui-universal/primitive-utilities';
import { withError, withValidation } from '@bingads-webui-react/validation';
import injectSheet from 'react-jss';

import { AddressModelShape, PassiveErrorShape } from './shape';
import { styles } from './css/styles';

const { CountryCode } = Constants;
const StatesOrProvinceField = 'StateOrProvince';
const AddressFields = ['Line1', 'Line2', 'City', 'PostalCode', StatesOrProvinceField];
const AddressFieldsWithLine3And4 = ['Line1', 'Line2', 'Line3', 'Line4', 'City', 'PostalCode', StatesOrProvinceField];
const AddressFieldsByPassValidation = ['Country', 'CountryName', 'StateOrProvinceName'];
const AddressFormProperties = ['Country', ...AddressFieldsWithLine3And4];

export const mapListForDropdown = (items, idName, valueName) => _.map(items, item => ({
  id: item[idName],
  value: item[valueName],
}));

// stringify and parse is for deep copy to make sure this.state remain unchanged
export const getNewState = (state, partialNewState) => merge(JSON.parse(JSON.stringify(state)), partialNewState);

export const selectOptions = options => options.map(({ id, value }) => <option key={id} value={id}>{value}</option>);

const WrappedDropdownSelector = withError(withValidation(DropdownSelector));
const WrappedDiv = withError(withValidation(props => <div {...props} />));

/**
 * this.state
 * @property {Array} countries - list of countries for dropdown
 * @property {Array} states - list of states for dropdown
 * @property {AddressField} Country - Country object
 * @property {AddressField} Line1 - Line1 object
 * @property {AddressField} Line2 - Line2 object
 * @property {AddressField} City - City object
 * @property {AddressField} StateOrProvince - StateOrProvince object
 * @property {AddressField} PostalCode - PostalCode object
*/

/**
 * AddressField
 * @property {String} field - field name('Line1', 'Line2', 'City', 'PostalCode', 'StateOrProvince')
 * @property {String} value - value of current field
 * @property {Number} order - inputbox order of current field
 * @property {Bool} isValid - is field valid for current field
 * @property {Object} metadata - metadata of current field(refer to @bingads-webui-clientcenter/address for more info)
*/

class UnwrappedAddressForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = { Country: { value: props.model.Country } };
    this.CustomizedFormGroup = props.showLineByLineFormGroup ? LineByLineFormGroup : InputFormGroup;
    if (props.helpPopupId.length !== 0) {
      this.helpPopupId = props.helpPopupId;
    } else {
      this.helpPopupId = props.isSignupFlow
        ? 'Pop_BA_Signup_BusinessLocationTaxInfo'
        : 'Pop_BA_Accounts_BusinessLocation';
    }
  }

  componentDidMount() {
    this.loadCountries();
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!_.isEqual(nextProps.model, this.props.model) && nextProps.model) {
      if (!this.props.model || !_.isEqual(nextProps.model.Country, this.props.model.Country)) {
        this.onCountryChange(nextProps.model.Country, true, nextProps.model);
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Limit to comparing the fields that will be called back, this can avoid the redundant callback due to countries set in state
    if (!_.isEqual(_.pick(prevState, AddressFormProperties), _.pick(this.state, AddressFormProperties))) {
      this.props.onAddressChange(this.getTrimedState(), this.getAddressObject());
    } else if (!_.isEqual(prevProps.model, this.props.model) && this.props.useModelAsState && this.props.model.City !== undefined) {
      // This is to allow setting prefill model values past the initial render
      // It looks like when this component is hidden, address data gets set to undefined in all fields
      // The City !== undefined comparison stops this from causing the duplicate keys in list react error
      // Below setState use should be allowed, discussion here https://github.com/yannickcr/eslint-plugin-react/issues/1707
      const isStateDropdown = !_.isEmpty(this.state.states);
      const newStateOrProvince = isStateDropdown && !_.findWhere(this.state.states, { id: this.props.model.StateOrProvince })
        ? _.first(this.state.states).id
        : this.props.model.StateOrProvince;
      const newStateOrProvinceName = _.get(_.findWhere(this.state.states, { id: newStateOrProvince }), 'value');

      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        ...prevState,
        Line1: {
          ...prevState.Line1,
          value: this.props.model.Line1,
        },
        Line2: {
          ...prevState.Line2,
          value: this.props.model.Line2,
        },
        Line3: {
          ...prevState.Line3,
          value: this.props.model.Line3,
        },
        Line4: {
          ...prevState.Line4,
          value: this.props.model.Line4,
        },
        City: {
          ...prevState.City,
          value: this.props.model.City,
        },
        PostalCode: {
          ...prevState.PostalCode,
          value: this.props.model.PostalCode,
        },
        StateOrProvince: {
          ...prevState.StateOrProvince,
          value: newStateOrProvince,
        },
        StateOrProvinceName: {
          ...prevState.StateOrProvinceName,
          value: newStateOrProvinceName,
        },
      });
    }
  }

  componentWillUnmount() {
    if (this.findCountriesCallToken) {
      this.findCountriesCallToken.cancel();
    }
    if (this.findStatesCallToken) {
      this.findStatesCallToken.cancel();
    }
  }

  onInputValueChange = ({ data, isValid }) => {
    const field = _.keys(data)[0];
    this.setState(state => getNewState(state, {
      [field]: {
        value: data[field],
        isValid,
      },
    }));
  };

  onStateChange = (state) => {
    this.setState(latestState => getNewState(latestState, {
      [StatesOrProvinceField]: {
        value: state,
        isValid: true,
      },
      StateOrProvinceName: {
        value: _.get(_.findWhere(this.state.states, { id: state }), 'value'),
      },
    }));
  };

  onCountryChange = (country, prefill, prefillModel) => {
    const countryId = country || this.props.market;
    this.findStatesCallToken = createAsyncToken(this.props.findStates(countryId))
      .on({
        onSuccess: (states) => {
          const newState = _.mapObject(Metadata[countryId] || Metadata.default, (metadata, field) => {
            const isRequired = this.props.isOptional ? false : metadata.required;
            return {
              field,
              value: this.getFieldInitValue(field, states, prefill, prefillModel),
              order: metadata.order,
              isValid: (field === StatesOrProvinceField && !_.isEmpty(states)) || !isRequired,
              metadata: {
                type: 'string',
                required: isRequired,
                maxLength: metadata.maxLength,
                pattern: metadata.match,
                label: metadata.label ? this.props.i18n.getString(metadata.label) : '',
              },
            };
          });
          _.defaults(newState, {
            Country: {
              value: countryId,
            },
            CountryName: {
              value: _.get(_.findWhere(this.state.countries, { id: countryId }), 'value'),
            },
            StateOrProvinceName: {
              value: _.get(_.findWhere(states, { Key: newState.StateOrProvince.value }), 'Value'),
            },
            states: mapListForDropdown(states, 'Key', 'Value'),
          }, this.state);
          this.setState(newState);
        },
      });
  };

  getLegacyI18n = () => buildLegacyI18n(this.props.i18n, ValidationLegacyStrings);

  /**
   * getFieldInitValue is called when country changes, including the first time address form is rendered with selected Country.
   * This function will make sure:
   * - When address-form is rendered for first time, it's prefilled with given model
   * - When user select country, form values are cleared
   * @param {String} field - Field name('Line1', 'Line2', 'City', 'PostalCode', 'StateOrProvince').
   * @param {Array} states - List of states or provinces.
   * @param {Boolean} prefill - Boolean indicates if we should prefill address form with model in props.
   * @param {object} prefillModel - Address object to be filled in the form
   * @returns {String} The value to be set for current field
  */
  getFieldInitValue(field, states, prefill, prefillModel) {
    let prefillValue = prefillModel ? (prefillModel[field] || '') : '';
    let clearValue = '';
    if (field === StatesOrProvinceField && !_.isEmpty(_.first(states))) {
      clearValue = _.first(states).Key;
      if (!_.any(states, { Key: prefillValue })) {
        prefillValue = clearValue;
      }
    }
    return prefill ? prefillValue : clearValue;
  }

  getTrimedState = () => {
    const pickedField = ['value', 'isValid'];
    const stateTrim = _.mapObject(_.pick(this.state, [...this.getAddressFields(), ...AddressFieldsByPassValidation]), value => _.pick(value, pickedField));
    return _.extend(stateTrim, { isValid: _.every(stateTrim, (value, field) => value.isValid || _.contains(AddressFieldsByPassValidation, field)) });
  };

  getAddressObject = () => _.mapObject(_.pick(this.state, [...this.getAddressFields(), 'Country']), field => ((field.isValid || !field.field) ? field.value : '')); // !field.field - Used for the `Country`, which doesn't have the field

  getAddressFields = () => {
    const showAddressLine3And4 = this.props.prefillModel.Line3 || this.props.prefillModel.Line4;
    if (showAddressLine3And4) {
      return AddressFieldsWithLine3And4;
    }
    return AddressFields;
  };

  mergeNewAddressObjectIntoState(addressObject) {
    const nextState = _.chain(this.state)
      .pick(this.getAddressFields())
      .mapObject((fieldValue, fieldName) => ({ ...fieldValue, value: addressObject[fieldName] || '' }))
      .value();
    this.setState(nextState);
  }

  updateCountriesInState = (countries) => {
    const countryList = mapListForDropdown(countries, 'Code', 'Name');
    const selectedCountry = _.findWhere(countryList, { id: this.props.model.Country });
    this.onCountryChange(selectedCountry ? selectedCountry.id : this.props.market, true, this.props.model);
    this.setState(state => getNewState(state, {
      countries: countryList,
    }));
  };

  loadCountries = () => {
    if (_.isEmpty(this.state.countries)) {
      if (_.isEmpty(this.props.countries)) {
        this.findCountriesCallToken = createAsyncToken(this.props.findCountries())
          .on({
            onSuccess: (countries) => {
              this.updateCountriesInState(countries);
            },
          });
      } else {
        this.updateCountriesInState(this.props.countries);
      }
    }
  };

  renderFieldInput = ({
    field,
    value,
    metadata,
    labelledBy,
    addLabel,
  }) => {
    let labelId;
    const ariaLabelledByArray = labelledBy;
    if (addLabel && _.isArray(ariaLabelledByArray) && _.first(ariaLabelledByArray)) {
      labelId = addLabel ? `${_.first(labelledBy)}-${field}-label` : undefined;
      ariaLabelledByArray.push(labelId);
    }
    const ariaLabelledBy = ariaLabelledByArray && ariaLabelledByArray.join(' ');
    const ariaLabel = (metadata && metadata.label) || '';
    return (
      <React.Fragment key={field}>
        {labelId && <label className={this.props.classes.hidden} id={labelId} >{ariaLabel}</label>}
        <Input
          aria-label={labelId ? undefined : ariaLabel}
          aria-labelledby={ariaLabelledBy}
          id={this.props.id + field}
          className="form-control"
          name={field}
          placeholder={(this.props.showPlaceholder && metadata && metadata.label) || ''}
          validationProps={{
            onValidate: this.onInputValueChange,
            field,
            data: { [field]: value },
            schema: { properties: { [field]: metadata } },
            i18n: this.getLegacyI18n(),
            alwaysShowError: this.props.alwaysShowError,
            ...this.props.passiveErrors[field],
          }}
        />
      </React.Fragment>
    );
  };

  renderDropdown = ({
    items,
    selectedId,
    onSelect,
    key,
    ariaLabel,
    addLabel,
    labelledBy,
  }) => {
    const domId = this.props.id + key;

    if (this.props.useDropdownList) {
      const selectedItem = _.findWhere(items, { id: selectedId });
      let labelId;
      const ariaLabelledByArray = labelledBy;
      if (addLabel && _.isArray(ariaLabelledByArray) && _.first(ariaLabelledByArray)) {
        labelId = addLabel ? `${_.first(labelledBy)}-${key}-label` : undefined;
        ariaLabelledByArray.push(labelId);
      }

      const ariaLabelledBy = ariaLabelledByArray && ariaLabelledByArray.join(' ');
      return (
        <React.Fragment key={key}>
          {labelId && <label className={this.props.classes.hidden} id={labelId} >{ariaLabel}</label>}
          <Select
            id={domId}
            aria-label={ariaLabelledBy ? undefined : ariaLabel}
            aria-labelledby={ariaLabelledBy}
            value={selectedItem ? selectedItem.id : _.first(items).id}
            onChange={({
              target: { value: newValue },
            }) => onSelect(newValue)}
            name={`address-${key}`}
            validationProps={{
              i18n: this.getLegacyI18n(),
              ...this.props.passiveErrors[key],
            }}
          >
            {selectOptions(items)}
          </Select>
        </React.Fragment>
      );
    }
    return (
      <div id={domId} key={key} >
        <WrappedDropdownSelector
          selectedItem={_.findWhere(items, { id: selectedId }) || _.first(items)}
          totalCount={items.length}
          items={items}
          onSelect={newValue => onSelect(newValue.id)}
          i18n={this.props.i18n}
          validationProps={{
            i18n: this.getLegacyI18n(),
            ...this.props.passiveErrors[key],
          }}
        />
      </div>
    );
  };

  renderStaticFormGroup = ({ items, contentColClass }) => (
    <StaticFormGroup id={`${this.props.id}Country`} className={`${this.props.id}CountryClassName`} contentColClass={contentColClass}>
      <div>{items[0].value}</div>
    </StaticFormGroup>
  );

  renderCountries = ({ groupLabelId, contentColClass, addLabel } = {}) => {
    if (!_.isEmpty(this.state.countries)) {
      if (this.state.countries.length === 1) {
        return this.renderStaticFormGroup({ items: this.state.countries, contentColClass });
      }
      const labelledBy = groupLabelId ? [groupLabelId] : [];

      return this.renderDropdown({
        items: this.state.countries,
        selectedId: this.state.Country.value,
        onSelect: this.onCountryChange,
        key: 'Country',
        ariaLabel: this.props.i18n.getString(_TL_('Countries/region dropdown')),
        labelledBy,
        addLabel,
      });
    }
    return null;
  };

  renderAddressForm = ({ contentColClass, groupLabelId } = {}) => _.chain(this.state)
    .pick(this.getAddressFields())
    .filter(fieldState => fieldState.field)
    .sortBy('order')
    .map((fieldState) => {
      const isStateDropdown = fieldState.field === StatesOrProvinceField && !_.isEmpty(this.state.states);
      const ariaLabel = (fieldState.metadata && fieldState.metadata.label) || '';
      const addLabel = !this.props.showFieldsLabel;
      const labelledBy = groupLabelId ? [groupLabelId] : [];
      const key = fieldState.field;
      if (this.props.showSectionLabel) {
        const { CustomizedFormGroup } = this;
        const fieldFormGroupId = (!addLabel && groupLabelId && `${groupLabelId}-field-${key}`) || '';
        if (fieldFormGroupId) {
          labelledBy.push(`${groupLabelId}-field-${key}-label`);
        }
        return (
          <CustomizedFormGroup
            id={fieldFormGroupId}
            className={`address-form-${fieldState.field}`}
            key={`Group${key}`}
            showLabel={this.props.showFieldsLabel}
            label={this.props.showFieldsLabel ? fieldState.metadata && fieldState.metadata.label : ''}
            contentColClass={contentColClass}
          >
            {isStateDropdown
              ? this.renderDropdown({
                items: this.state.states,
                selectedId: this.state.StateOrProvince.value,
                onSelect: this.onStateChange,
                key,
                ariaLabel,
                labelledBy,
                addLabel,
              })
              : this.renderFieldInput({
                field: key,
                value: fieldState.value,
                metadata: fieldState.metadata,
                labelledBy,
                addLabel,
              })}
          </CustomizedFormGroup>);
      }
      if (isStateDropdown) {
        return this.renderDropdown({
          items: this.state.states,
          selectedId: this.state.StateOrProvince.value,
          onSelect: this.onStateChange,
          key,
          ariaLabel,
          labelledBy,
          addLabel,
        });
      }
      return this.renderFieldInput({
        field: key,
        value: fieldState.value,
        metadata: fieldState.metadata,
        labelledBy,
        addLabel,
      });
    })
    .value();

  renderContent() {
    const groupId = this.props.formGroupId || `${this.props.id}-formGroup`;
    const groupLabelId = `${groupId}-label`;
    const { CustomizedFormGroup } = this;
    if (this.props.showSectionLabel && !_.isEmpty(this.state.countries) && !this.props.hideSingleCountry) {
      if (this.props.showLineByLineFormGroup) {
        const contentColClass = 'col-xs-12';
        return (
          <Fragment>
            <CustomizedFormGroup
              id={groupId}
              className={classnames('address-form-location', this.props.classes.multiLineFormGroup)}
              label={this.props.i18n.getString(_TL_('Business location'))}
              helpPopupTopic={this.helpPopupId}
              helpPopupAriaLabel={this.props.helpPopupAriaLabel}
              isMuliInputFormGroup
              contentColExtraClass={this.props.classes.multiLineInput}
            >
              <CustomizedFormGroup
                className="address-form-country"
                contentColClass={contentColClass}
                showLabel={false}
              >
                {this.renderCountries({ contentColClass, groupLabelId, addLabel: true })}
              </CustomizedFormGroup>
              {this.renderAddressForm({ contentColClass, groupLabelId })}
            </CustomizedFormGroup>
          </Fragment>
        );
      }

      return (
        <Fragment>
          <CustomizedFormGroup
            id={groupId}
            className={classnames('address-form-location', this.props.classes.multiLineFormGroup)}
            label={this.props.i18n.getString(_TL_('Business location'))}
            helpPopupTopic={this.helpPopupId}
            helpPopupAriaLabel={this.props.helpPopupAriaLabel}
          >
            {this.renderCountries()}
          </CustomizedFormGroup>
          {this.renderAddressForm()}
        </Fragment>
      );
    }
    if (this.props.hideSingleCountry && !_.isEmpty(this.state.countries) && this.state.countries.length === 1) {
      return this.renderAddressForm({ groupLabelId });
    }
    return (
      <Fragment>
        {this.renderCountries({ groupLabelId, addLabel: true })}
        {this.renderAddressForm({ groupLabelId })}
      </Fragment>
    );
  }

  render() {
    return (
      <WrappedDiv
        className={classnames('address-form', this.props.className)}
        id={this.props.id}
        key={this.state.Country.value}
        validationProps={{
          i18n: this.getLegacyI18n(),
          ...this.props.passiveErrors.Outer,
        }}
      >
        {this.renderContent()}
      </WrappedDiv>
    );
  }
}

UnwrappedAddressForm.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  id: PropTypes.string.isRequired,
  market: PropTypes.string,
  findCountries: PropTypes.func,
  countries: PropTypes.arrayOf(PropTypes.shape({
    Code: PropTypes.string.isRequired,
    Name: PropTypes.string,
  })),
  findStates: PropTypes.func.isRequired,
  onAddressChange: PropTypes.func.isRequired,
  model: AddressModelShape,
  prefillModel: AddressModelShape,
  className: PropTypes.string,
  showSectionLabel: PropTypes.bool,
  showFieldsLabel: PropTypes.bool,
  hideSingleCountry: PropTypes.bool,
  isSignupFlow: PropTypes.bool,
  useDropdownList: PropTypes.bool,
  showLineByLineFormGroup: PropTypes.bool,
  showPlaceholder: PropTypes.bool,
  alwaysShowError: PropTypes.bool,
  helpPopupId: PropTypes.string,
  helpPopupAriaLabel: PropTypes.string,
  useModelAsState: PropTypes.bool,
  passiveErrors: PropTypes.shape({
    Line1: PassiveErrorShape,
    Line2: PassiveErrorShape,
    Line3: PassiveErrorShape,
    Line4: PassiveErrorShape,
    PostalCode: PassiveErrorShape,
    City: PassiveErrorShape,
    StateOrProvince: PassiveErrorShape,
    Country: PassiveErrorShape,
    Outer: PassiveErrorShape,
  }),
  classes: PropTypes.shape({
    multiLineInput: PropTypes.string,
    multiLineFormGroup: PropTypes.string,
    hidden: PropTypes.string,
  }).isRequired,
  formGroupId: PropTypes.string,
  isOptional: PropTypes.bool,
};

UnwrappedAddressForm.defaultProps = {
  model: {},
  prefillModel: {},
  market: CountryCode.US,
  findCountries: null,
  countries: [],
  className: null,
  showSectionLabel: false,
  showFieldsLabel: false,
  hideSingleCountry: false,
  isSignupFlow: false,
  useDropdownList: false,
  showLineByLineFormGroup: false,
  showPlaceholder: false,
  alwaysShowError: false,
  helpPopupId: '',
  helpPopupAriaLabel: '',
  useModelAsState: false,
  passiveErrors: {},
  formGroupId: null,
  isOptional: false,
};

export const AddressForm = injectSheet(styles)(UnwrappedAddressForm);
